import { getResourceRichTextError } from './resource-rich-text';

// Recognize editor and unsafe HTML tags while preserving legacy placeholders such as <patient>.
// Keep supported formatting tags aligned with the TEXT sanitizer; literal known tags remain ambiguous.
const questionLabelHtmlPattern =
  /<\/?(?:p|br|strong|b|u|script|style|template|head|iframe|object|embed|img|picture|svg|image|video|audio|textarea|option|noscript|div|span|h[1-6]|ul|ol|li|a|em|i|s|strike|mark|blockquote|hr)(?=[\s/>])[^>]*>/i;

export const hasQuestionLabelHtml = (value: string): boolean =>
  questionLabelHtmlPattern.test(value);

export const hiddenRichTextTags = [
  'script',
  'style',
  'template',
  'head',
  'iframe',
  'object',
  'embed',
  'textarea',
  'option',
  'noscript',
];

/** Remove hidden contents when extracting visible text or appending a label suffix. */
export const parseQuestionLabelHtml = (value: string): Document => {
  const doc = new DOMParser().parseFromString(value, 'text/html');
  doc
    .querySelectorAll(hiddenRichTextTags.join(', '))
    .forEach((node) => node.remove());
  return doc;
};

/** Plain text for validation, accessible names and compact builder summaries. */
export const questionLabelText = (value: string = ''): string => {
  if (!hasQuestionLabelHtml(value)) return value;
  const doc = parseQuestionLabelHtml(value);
  doc
    .querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, br')
    .forEach((node) => {
      node.before(doc.createTextNode(' '));
      node.after(doc.createTextNode(' '));
    });
  return (doc.body.textContent ?? '').replace(/\s+/gu, ' ').trim();
};

export const hasVisibleQuestionLabel = (value: unknown): boolean =>
  typeof value === 'string' &&
  !!questionLabelText(value).replace(
    /[\s\u200B-\u200D\u2060\u00AD\uFEFF]/gu,
    ''
  ) &&
  (!hasQuestionLabelHtml(value) || !getResourceRichTextError(value));

/** Escape plain legacy labels before passing them to the HTML editor. */
export const questionLabelEditorContent = (value: string = ''): string =>
  hasQuestionLabelHtml(value)
    ? value
    : `<p>${value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r\n?|\n/g, '</p><p>')}</p>`;
